import React, { useEffect, useRef } from "react";
import {
  Alert,
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import { nuclearn_api_url } from "src/App";
import { useDatasetArchiveStream } from "./useDatasetArchiveStream";

export interface DatasetArchiveCompletion {
  downloadUrl: string;
  filename: string;
  sizeBytes: number | null;
}

export interface DatasetArchiveProgressProps {
  datasetId: number;
  correlationId: string;
  onComplete: (completion: DatasetArchiveCompletion) => void;
  onDismiss: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  pending: "Starting…",
  reading_metadata: "Reading dataset metadata…",
  building_zip: "Building archive…",
  uploading_to_storage: "Uploading to storage…",
};

const describeStage = (stage: string): string =>
  STAGE_LABELS[stage] ?? "Working…";

const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const resolveAbsoluteUrl = (relativeOrAbsolute: string): string => {
  if (/^https?:\/\//i.test(relativeOrAbsolute)) return relativeOrAbsolute;
  const base = nuclearn_api_url ?? window.location.origin;
  const absoluteBase = base.startsWith("/")
    ? `${window.location.origin}${base}`
    : base;
  return `${absoluteBase}${relativeOrAbsolute}`;
};

const ProgressCounter: React.FC<{
  processed: number;
  total: number;
  bytesWritten: number | null;
}> = ({ processed, total, bytesWritten }) => {
  const countText = total > 0 ? `${processed} / ${total} artifacts` : "…";
  const byteText = bytesWritten ? ` · ${formatBytes(bytesWritten)}` : "";
  return (
    <Typography variant="caption" color="text.secondary">
      {countText}
      {byteText}
    </Typography>
  );
};

const RunningBody: React.FC<{
  stage: string;
  percent: number;
  processed: number;
  total: number;
  bytesWritten: number | null;
  onCancel: () => void;
}> = ({ stage, percent, processed, total, bytesWritten, onCancel }) => (
  <Stack spacing={1}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2" sx={{ flexGrow: 1 }}>
        {describeStage(stage)}
      </Typography>
      <Tooltip title="Cancel">
        <IconButton size="small" onClick={onCancel} aria-label="Cancel">
          <CancelIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
    <LinearProgress
      variant={percent > 0 ? "determinate" : "indeterminate"}
      value={percent}
    />
    <ProgressCounter
      processed={processed}
      total={total}
      bytesWritten={bytesWritten}
    />
  </Stack>
);

const FailedBody: React.FC<{ error: string; onClose: () => void }> = ({
  error,
  onClose,
}) => (
  <Alert
    severity="error"
    action={
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
    }
  >
    {error}
  </Alert>
);

const DatasetArchiveProgress: React.FC<DatasetArchiveProgressProps> = ({
  datasetId,
  correlationId,
  onComplete,
  onDismiss,
}) => {
  const stream = useDatasetArchiveStream({ datasetId, correlationId });
  // Stores the correlationId we've already fired onComplete for. Using the
  // id itself (rather than a boolean + reset effect) keeps the effect
  // idempotent across StrictMode's dev-mode double-invoke: the second run
  // reads the same id the first run wrote and short-circuits.
  const notifiedCorrelationId = useRef<string | null>(null);

  useEffect(() => {
    if (
      stream.status === "ready" &&
      stream.downloadUrl &&
      stream.filename &&
      notifiedCorrelationId.current !== correlationId
    ) {
      notifiedCorrelationId.current = correlationId;
      onComplete({
        downloadUrl: resolveAbsoluteUrl(stream.downloadUrl),
        filename: stream.filename,
        sizeBytes: stream.sizeBytes,
      });
    }
  }, [
    correlationId,
    stream.status,
    stream.downloadUrl,
    stream.filename,
    stream.sizeBytes,
    onComplete,
  ]);

  const handleCancel = () => {
    stream.cancel();
    onDismiss();
  };

  if (stream.status === "ready" || stream.status === "cancelled") {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{ p: 2, mt: 1, borderRadius: 2, minWidth: 320 }}
      data-testid="dataset-archive-progress"
    >
      {stream.status === "failed" ? (
        <FailedBody
          error={stream.error ?? "Archive build failed"}
          onClose={onDismiss}
        />
      ) : (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Preparing download
          </Typography>
          <RunningBody
            stage={stream.stage}
            percent={stream.percent}
            processed={stream.processed}
            total={stream.total}
            bytesWritten={stream.bytesWritten}
            onCancel={handleCancel}
          />
        </Box>
      )}
    </Paper>
  );
};

export default DatasetArchiveProgress;
